import { useCallback, useEffect, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';
import { TransactionItem, TransactionsResponse } from './types';

const PAGE_SIZE = 25;

/**
 * Paginated coin-ledger history for one user, newest first, optionally filtered
 * by `source_type`. Offset paging matches the endpoint; changing the filter
 * resets to the first page. `source_types` comes back with each page and lists
 * only the types this user actually has, so it drives the filter dropdown.
 */
export function useUserTransactions(env: Env, userId: string) {
  const { request } = useAdminApi();
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [sourceTypes, setSourceTypes] = useState<string[]>([]);
  const [sourceType, setSourceType] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refundWindowDays, setRefundWindowDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (nextOffset: number, nextSourceType: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await request<TransactionsResponse>({
          env,
          path: `/admin/gamification/users/${userId}/transactions`,
          query: {
            limit: PAGE_SIZE,
            offset: nextOffset,
            source_type: nextSourceType || undefined,
          },
        });
        setItems(data.items);
        setHasMore(data.has_more);
        setOffset(data.offset);
        setRefundWindowDays(data.refund_window_days ?? 90);
        // Keep the unfiltered list — a filtered page only reports its own type.
        if (!nextSourceType) setSourceTypes(data.source_types ?? []);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not load transactions');
        setItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [request, env, userId],
  );

  useEffect(() => {
    load(0, '');
  }, [load]);

  const applyFilter = useCallback(
    (next: string) => {
      setSourceType(next);
      load(0, next);
    },
    [load],
  );

  const nextPage = useCallback(() => {
    if (hasMore) load(offset + PAGE_SIZE, sourceType);
  }, [hasMore, offset, sourceType, load]);

  const prevPage = useCallback(() => {
    if (offset > 0) load(Math.max(0, offset - PAGE_SIZE), sourceType);
  }, [offset, sourceType, load]);

  const reload = useCallback(() => load(offset, sourceType), [load, offset, sourceType]);

  return {
    items,
    sourceTypes,
    sourceType,
    offset,
    pageSize: PAGE_SIZE,
    hasMore,
    refundWindowDays,
    loading,
    error,
    applyFilter,
    nextPage,
    prevPage,
    reload,
  };
}
